import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './SignUp.css';

const SignUp = () => {
  const navigate = useNavigate();
  const { signup } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    countryCode: '+91'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Please enter your full name');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email address');
      return;
    }
    if (!formData.password) {
      setError('Please enter a password');
      return;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!agreedToTerms) {
      setError('Please agree to the Terms & Privacy Policy');
      return;
    }

    setLoading(true);

    try {
      // Call signup (will also call login API to determine user status)
      const result = await signup({
        name: formData.name.trim(),
        phone_number: `${formData.countryCode} ${formData.phone.trim()}`,
        email: formData.email.trim(),
        password: formData.password
      });

      if (result.success) {
        // Navigate based on user_status and user_type
        const { access } = result;

        if (access?.hasAccess) {
          // User is approved (ACTIVE or INACTIVE) - go to dashboard
          navigate('/dashboard');
        } else if (access?.shouldShowPending) {
          // User is NOT APPROVED - show pending page
          navigate('/approval-pending');
        } else if (access?.errorMessage) {
          // Invalid user type - show access denied
          navigate('/access-denied');
        } else {
          // Default to approval pending for new users
          navigate('/approval-pending');
        }
      } else {
        // Show user-friendly error message from API
        setError(result.error || result.message || 'Sign up failed. Please try again.');
      }
    } catch (err) {
      // Handle unexpected errors
      if (err.message?.includes('Network') || err.message?.includes('fetch')) {
        setError('Unable to connect. Please check your internet connection.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-content">
        {/* Header */}
        <header className="signup-header">
          <div className="logo-container">
            <img src="/avro-logo-nobg.png" alt="Avro Recycling" className="logo-img" />
          </div>
          <h1 className="brand-title">Join Avro Recycling</h1>
          <p className="brand-subtitle">Create your picker account</p>
        </header>

        {/* Form Card */}
        <div className="signup-card">
          {/* Tab switcher */}
          <div className="auth-tabs">
            <Link to="/login" className="auth-tab">Login</Link>
            <Link to="/signup" className="auth-tab active">Sign Up</Link>
          </div>

          {error && (
            <div className="alert alert-error">
              <svg className="alert-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="signup-form">
            {/* Full Name */}
            <div className="form-field">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
              />
            </div>

            {/* Phone Number */}
            <div className="form-field">
              <label htmlFor="phone">Phone Number</label>
              <div className="phone-group">
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                  className="country-select"
                >
                  <option value="+91">🇮🇳 +91</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+44">🇬🇧 +44</option>
                </select>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-field">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div className="form-field">
              <label htmlFor="password">Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              <span className="field-hint">Minimum 8 characters</span>
            </div>

            {/* Terms Checkbox */}
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <span className="checkmark"></span>
              <span className="checkbox-text">
                I agree to the <a href="#terms">Terms of Service</a> & <a href="#privacy">Privacy Policy</a>
              </span>
            </label>

            {/* Submit Button */}
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <span className="loading-spinner"></span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="card-footer">
            <p>Already have an account? <Link to="/login">Login</Link></p>
          </div>
        </div>

        {/* Footer */}
        <footer className="signup-footer">
          <p>© 2026 Avro Recycling. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default SignUp;
